import DeviceUtility from "./device";
import { rotateBlob } from "@common-ui/media/rotateBlob";

class Snapshot {
	/**
	 * Detects and removes black bars from the left and right sides of a canvas
	 * @param {HTMLCanvasElement} canvas - The canvas to process
	 * @returns {HTMLCanvasElement} - Either the original canvas or a new cropped canvas
	 */
	static removeBlackBars(canvas) {
		const width = canvas.width;
		const height = canvas.height;
		if (!width || !height) return canvas;

		const canvasContext = canvas.getContext("2d");
		if (!canvasContext) return canvas;

		const imageData = canvasContext.getImageData(0, 0, width, height);
		const data = imageData.data;

		const isNonBlack = (x, y, threshold = 8) => {
			const idx = (y * width + x) * 4;
			return data[idx] > threshold || data[idx + 1] > threshold || data[idx + 2] > threshold;
		};

		const numSamples = Math.min(25, Math.max(15, Math.floor(height / 50)));
		const sampleYs = Array.from({ length: numSamples }, (_, i) => Math.floor(height * (0.05 + (0.9 * i) / (numSamples - 1))));

		const quickSampleYs = [Math.floor(height * 0.3), Math.floor(height * 0.5), Math.floor(height * 0.7)];
		const hasContentNearLeftEdge = quickSampleYs.some((y) => [0, 1, 2, 3, 4].some((x) => isNonBlack(x, y)));
		const hasContentNearRightEdge = quickSampleYs.some((y) => [width - 1, width - 2, width - 3, width - 4, width - 5].some((x) => isNonBlack(x, y)));

		// Early exit: if both edges clearly have content, there are no vertical black bars to remove.
		if (hasContentNearLeftEdge && hasContentNearRightEdge) {
			return canvas;
		}

		// Limit scan to avoid treating dark video content as bars, but allow very wide pillarbox bars.
		// Capped to just under half-width so we always have some content region remaining.
		const maxBarWidth = Math.max(1, Math.min(Math.floor(width / 2) - 1, Math.floor(width * 0.45)));

		// Per-row boundary detection: find where each sample row transitions from black to content.
		// Real pillarbox bars have a straight vertical edge so all rows agree on the boundary position.
		// Dark video content (e.g. title cards) will either have no content in most rows or have
		// inconsistent boundaries across rows.
		const leftBoundsPerRow = sampleYs.map((y) => {
			// Inclusive scan so bars exactly maxBarWidth pixels wide still detect the first content pixel.
			for (let x = 0; x <= maxBarWidth; x++) {
				if (isNonBlack(x, y)) return x;
			}
			return -1;
		});

		const rightBoundsPerRow = sampleYs.map((y) => {
			// Inclusive scan so bars exactly maxBarWidth pixels wide still detect the last content pixel.
			for (let x = width - 1; x >= width - 1 - maxBarWidth; x--) {
				if (isNonBlack(x, y)) return x;
			}
			return -1;
		});

		// Validate that detected boundaries represent a real bar edge:
		// 1. Content coverage: at least 70% of sample rows must have content past the boundary
		// 2. Edge consistency: at least 80% of valid boundaries must agree on position (straight edge)
		const minRowCoverage = 0.7;
		const minEdgeConsistency = 0.8;
		const maxEdgeDeviation = Math.max(3, Math.floor(width * 0.005));

		const findValidBarEdge = (boundsPerRow) => {
			const validBounds = boundsPerRow.filter((bound) => bound >= 0);
			if (validBounds.length < sampleYs.length * minRowCoverage) return null;

			const sorted = [...validBounds].sort((a, b) => a - b);
			const median = sorted[Math.floor(sorted.length / 2)];
			const consistentCount = validBounds.filter((bound) => Math.abs(bound - median) <= maxEdgeDeviation).length;
			if (consistentCount < validBounds.length * minEdgeConsistency) return null;

			return median;
		};

		const leftEdge = findValidBarEdge(leftBoundsPerRow);
		const rightEdge = findValidBarEdge(rightBoundsPerRow);

		const effectiveLeft = leftEdge !== null && leftEdge > width * 0.005 ? leftEdge : 0;
		const effectiveRight = rightEdge !== null && width - 1 - rightEdge > width * 0.005 ? rightEdge : width - 1;

		const contentWidth = effectiveRight - effectiveLeft + 1;
		const shouldCropBothSides = effectiveLeft > 0 && effectiveRight < width - 1;
		const shouldCropRightOnly = effectiveLeft === 0 && effectiveRight < width - 1 && hasContentNearLeftEdge;
		const shouldCropLeftOnly = effectiveLeft > 0 && effectiveRight === width - 1 && hasContentNearRightEdge;

		if (contentWidth < width * 0.99 && (shouldCropBothSides || shouldCropRightOnly || shouldCropLeftOnly)) {
			const croppedCanvas = document.createElement("canvas");
			croppedCanvas.width = contentWidth;
			croppedCanvas.height = height;
			const croppedContext = croppedCanvas.getContext("2d");
			if (!croppedContext) return canvas;
			croppedContext.drawImage(canvas, effectiveLeft, 0, contentWidth, height, 0, 0, contentWidth, height);
			return croppedCanvas;
		}

		return canvas;
	}
	static takeSnapshot(video, width, height, mirror, toBlob = true) {
		return new Promise(function (resolve, reject) {
			// Draw the 'video' element onto a temporary canvas.
			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;
			const canvasContext = canvas.getContext("2d");
			if (mirror) {
				canvasContext.translate(width, 0);
				canvasContext.scale(-1, 1);
			}
			canvasContext.drawImage(video, 0, 0, width, height);

			// Remove black bars using the reusable library function
			const finalCanvas = Snapshot.removeBlackBars(canvas);

			// Convert the canvas to a blob, and return the blob.
			if (toBlob) {
				finalCanvas.toBlob(resolve, "image/png");
			} else {
				resolve(finalCanvas.toDataURL("image/png"));
			}
		});
	}

	static async prepareForCropping(element) {
		let iosFallback = DeviceUtility.isIOS;
		let image = null;

		// Fallback for iOS CORS issue. The lightbox in combination with cropperjs does not play nicely
		// and the image fails CORS check because of caching so we use fetch to get the image and load it as a blob.
		if (iosFallback) {
			let src = element.src;
			const param = src.indexOf("?") == -1 ? `?v=${Date.now()}` : `&v=${Date.now()}`;
			src = `${src}${param}`;
			const response = await fetch(src, {
				"Cache-Control": "no-cache"
			});
			const blob = await response.blob();
			const base64 = URL.createObjectURL(blob);
			image = document.createElement("img");
			image.src = base64;
		} else {
			image = element.cloneNode();
			image.crossOrigin = "anonymous";
		}

		return image;
	}

	/**
	 * Rotates a blob by 90 degrees clockwise
	 *
	 * @param {Blob} blob - The blob to rotate
	 * @param {Object} [options={}] - The options for the rotation
	 * @param {string} [options.type] - The output blob type. Defaults to the input blob type.
	 * @returns {Promise<Blob>} - The rotated blob
	 */
	static rotateBlob(blob, options = {}) {
		// The shared implementation; this method stays because the MediaLightBox specs mock it.
		return rotateBlob(blob, options);
	}
}

export default Snapshot;
